import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import DocumentUploader from "../../component/DocumentUploader";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import {selectInitialized, selectLoading, selectQueryError} from "../../redux/documentsSlice";
import {loadDocumentsAsync} from "../../redux/documentsThunk";
import {selectPersistenceInitialized} from "../../redux/persistenceSlice";
import {selectHasValueOrders} from "../../redux/valueOrdersSlice";
import {useEffectRequiresValidRevaId} from "../../util/hooks";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";
import DocumentsTable from "./DocumentsTable";

/**
 * Tab for displaying documents of the current order.
 * @returns {React.JSX.Element}
 */
function TabDocuments() {
    const {t} = useTranslation();

    const dispatch = useDispatch();

    const loading = useSelector(selectLoading);
    const initialized = useSelector(selectInitialized);
    const queryError = useSelector(selectQueryError);

    const persistenceInitialized = useSelector(selectPersistenceInitialized);
    const hasValueOrders = useSelector(selectHasValueOrders);

    useEffectRequiresValidRevaId();

    useEffect(() => {
        if (persistenceInitialized && !initialized && !loading) {
            // Initially load table, after persistence was initialized, if documents have not been initialized/loading yet
            dispatch(loadDocumentsAsync());
        }
    }, [persistenceInitialized, initialized, loading, dispatch]);

    const reloadDocuments = () => {
        dispatch(loadDocumentsAsync());
    };

    return (
        <main className="grow pt-4 px-6 py-6 w-full">
            <div className="flex flex-col w-full h-full">
                <h2 className="flex mt-0">
                    <div className="flex-1 HeaderText">{t("documents.documents")}:</div>
                    <div className="HeaderToolbarRight flex">
                        <Button
                            id="refreshDocumentList"
                            label={t("documents.refresh")}
                            icon={PrimeIcons.REFRESH}
                            disabled={!hasValueOrders || loading}
                            loading={loading}
                            onClick={reloadDocuments}
                        />
                        <DocumentUploader />
                    </div>
                </h2>
                <PersistenceFinalMessage />
                <SnapshotMessage />
                <LoadingIndicator loading={loading || !initialized} />
                <ErrorIndicator errorObject={queryError} titleI18nKey="error.error" />
                <DocumentsTable />
            </div>
        </main>
    );
}

export default TabDocuments;
